import React, { useState, useEffect, useMemo } from 'react';
import { MenuItem, Select, ListSubheader } from '@mui/material';
import CustomPaper from '../common/CustomPaper';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import AIPlaceholderItem from './AIPlaceholderItem';
import useAIAssistantTemplateStore, { getAIAssistantTemplateStore } from './useAIAssistantTemplateStore';
import { send_request } from '../../utils/Request';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import FormMentionEditor from '../Component/FormMentionEditor';
import { validURL } from '../../utils/CanvasUtil';
import ImageInputComponent from './ImageInputComponent';
import ImageIcon from '@mui/icons-material/Image';
import LanguageIcon from '@mui/icons-material/Language';
import AIAssistantTemplateChips from './AIAssistantTemplateChips';
import Divider from '@mui/material/Divider';
import { MODEL_CONFIG, AVAILABLE_MODELS, DEFAULT_MODEL } from '../../utils/ModelConfig';

const SECTION_PADDING = { paddingTop: 16, paddingBottom: 8 };
const SUBSECTION_PADDING = { paddingTop: 8, paddingBottom: 8 };
const DESCRIPTION_STYLE = { color: 'var(--mui-palette-text-secondary)', fontSize: 14 };
const EDITOR_INPUT_GAP = 12;
const LOGO_STYLE = { width: 20, height: 20, marginRight: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
const CAPABILITY_ICON_STYLE = { marginLeft: 4, color: 'var(--mui-palette-text-secondary)' };

const AIAssistantTemplate = (props) => {
  const [loading, setLoading] = useState(false);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);

  const store = useMemo(() => getAIAssistantTemplateStore(props.component_id), [props.component_id]);

  const { availablePlaceholders } = props;

  const getValidModel = (modelToCheck, fallback = DEFAULT_MODEL) => {
    if (AVAILABLE_MODELS.includes(modelToCheck)) return modelToCheck;
    if (AVAILABLE_MODELS.includes(fallback)) return fallback;
    const firstValidModel = AVAILABLE_MODELS.find((model) =>
      Object.values(MODEL_CONFIG).some((provider) =>
        Object.prototype.hasOwnProperty.call(provider, model)
      )
    );
    return firstValidModel || AVAILABLE_MODELS[0];
  };

  const keyAuto = () => {
    return 'ai_assistant' + (Math.floor(Math.random() * 90000000) + 10000).toString();
  };

  const addEmptyPlaceholderValue = () => {
    let outputPlaceholders = JSON.parse(
      JSON.stringify(store.getState().data.aiAssistantData.outputPlaceholders)
    );
    const newPlaceholder = {
      desc: '',
      key: keyAuto(),
      type: 'text',
    };
    outputPlaceholders.push(newPlaceholder);
    store.getState().setAIOutputPlaceholders(outputPlaceholders);
  };

  const deletePlaceholders = (index) => {
    if (index !== -1) {
      let outputPlaceholders = JSON.parse(
        JSON.stringify(store.getState().data.aiAssistantData.outputPlaceholders)
      );
      outputPlaceholders.splice(index, 1);
      store.getState().setAIOutputPlaceholders(outputPlaceholders);
    }
  };

  const addChip = (text, isNotPlaceholder) => {
    if (text && text.trim() !== '') {
      if (isNotPlaceholder !== true) {
        text = '${' + text + '}';
      } else if (!validURL(text)) return;
      store.getState().setImageContext(text);
    }
  };

  const applyTemplate = (template) => {
    if (selectedTemplateId === template.id) {
      clearTemplate();
      return;
    }

    const storeState = store.getState();
    storeState.setGptModel(template.model);
    storeState.setTask(template.task);
    storeState.setContext(template.context);
    storeState.clearImageContext();
    storeState.setAIOutputPlaceholders(template.outputPlaceholders);
    setSelectedTemplateId(template.id);
  };

  const clearTemplate = () => {
    const modelToUse = getValidModel(DEFAULT_MODEL);
    const storeState = store.getState();
    storeState.setGptModel(modelToUse);
    storeState.setTask('');
    storeState.setContext('');
    storeState.clearImageContext();

    const defaultPlaceholder = {
      desc: '',
      key: keyAuto(),
      type: 'text',
    };
    storeState.setAIOutputPlaceholders([defaultPlaceholder]);
    setSelectedTemplateId(null);
  };

  useEffect(() => {
    if (props.component_id === undefined) return;

    if (store.getState().loaded) {
      props.onComponentNameChanged(store.getState().name);
      props.onLastModifiedChanged(store.getState().lastModified);
      setLoading(false);
      props.setChildTemplateLoaded(true);
      return;
    }

    setLoading(true);
    send_request(
      `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
      '',
      {}
    )
      .then((response) => {
        if (response && response.data) {
          const { components } = response.data;
          if (components) {
            store.getState().setAiAssistantVariables(components[0]);
            store.setState({ loaded: true });
            props.onComponentDescriptionChanged(components[0].description);
            props.onComponentNameChanged(store.getState().name);
            props.onLastModifiedChanged(store.getState().lastModified);
          }
        }

        setLoading(false);
        props.setChildTemplateLoaded(true);
      })
      .catch((err) => {
        throw Error(err.message);
      });
  }, []);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      (s) => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (placeholder && placeholder.selected !== undefined) {
          const { key, isNotPlaceholder } = placeholder.selected;
          addChip(key, isNotPlaceholder);
        }
      }
    );
    return unsub;
  }, []);

  const data = store((s) => s.data);
  const aiAssistantData = data?.aiAssistantData
    ? JSON.parse(JSON.stringify(data.aiAssistantData))
    : null;

  const placeholderTypeMap = ['Text', 'Number', 'HTML', 'JSON', 'List', 'DateTime', 'Boolean'];

  const getModelInfo = (modelId) => {
    for (const [, models] of Object.entries(MODEL_CONFIG)) {
      if (models[modelId]) {
        return {
          logoPath: models.logoPath,
          label: models[modelId].label,
          supportsImages: models[modelId].supportsImages,
          webAccess: models[modelId].webAccess,
        };
      }
    }
    return null;
  };

  const renderCapabilityIcons = (supportsImages, webAccess) => (
    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
      {supportsImages && <ImageIcon fontSize="small" style={CAPABILITY_ICON_STYLE} />}
      {webAccess && <LanguageIcon fontSize="small" style={CAPABILITY_ICON_STYLE} />}
    </div>
  );

  const renderModelMenuItems = () => {
    const legacyModels = [];

    const providerGroups = Object.entries(MODEL_CONFIG).map(([provider, models]) => {
      const logoPath = models.logoPath;

      const regularModels = Object.entries(models)
        .filter(([key, config]) => key !== 'logoPath' && !config.legacy)
        .map(([modelId, config]) => (
          <MenuItem key={modelId} value={modelId} style={{ display: 'flex', alignItems: 'center', padding: '8px 16px' }}>
            <img src={logoPath} alt={`${provider} logo`} style={LOGO_STYLE} />
            {config.label}
            {config.isDefault ? ' (default)' : ''}
            {renderCapabilityIcons(config.supportsImages, config.webAccess)}
          </MenuItem>
        ));

      Object.entries(models)
        .filter(([key, config]) => key !== 'logoPath' && config.legacy)
        .forEach(([modelId, config]) => {
          legacyModels.push(
            <MenuItem key={modelId} value={modelId} style={{ display: 'flex', alignItems: 'center', padding: '8px 16px' }}>
              <img src={logoPath} alt={`${provider} logo`} style={LOGO_STYLE} />
              {config.label}
              {renderCapabilityIcons(config.supportsImages, config.webAccess)}
            </MenuItem>
          );
        });

      if (regularModels.length > 0) {
        return [
          <ListSubheader key={provider} style={{ display: 'flex', alignItems: 'center', padding: '8px 16px' }}>
            <img src={logoPath} alt={`${provider} logo`} style={LOGO_STYLE} />
            {provider}
          </ListSubheader>,
          ...regularModels,
        ];
      }
      return [];
    });

    if (legacyModels.length > 0) {
      providerGroups.push([<ListSubheader key="LEGACY">LEGACY</ListSubheader>, ...legacyModels]);
    }

    return providerGroups;
  };

  const formMentionEditorProps = {
    placeholder: '',
    style: {
      width: '100%',
      verticalAlign: 'top',
      minHeight: '16px',
      fontSize: '14px',
    },
    onFocus: () => {},
    aiAssistant: true,
    contentBlock: false,
    hideScrollBar: true,
    assignTask: true,
  };

  const resolvedPlaceholders = availablePlaceholders != null
    ? { availablePlaceholders: availablePlaceholders[0].placeholders }
    : {};

  if (!loading && aiAssistantData) {
    const modelInfo = getModelInfo(aiAssistantData.gptModel);
    const modelSupportsImages = modelInfo?.supportsImages ?? false;

    return (
      <Grid>
        <CustomPaper>
          <Grid container style={{ paddingLeft: 16, paddingBottom: 28, paddingRight: 8 }}>

            {/* Templates */}
            <Grid item xs={12}>
              <AIAssistantTemplateChips
                onSelectTemplate={applyTemplate}
                selectedTemplateId={selectedTemplateId}
              />
              <Divider style={{ marginTop: 16 }} />
            </Grid>

            {/* Model */}
            <Grid item xs={12} style={SECTION_PADDING}>
              <span><b>Model</b></span>
            </Grid>
            <Grid item xs={12}>
              <Select
                style={{ background: 'var(--mui-palette-background-paper)', color: 'var(--mui-palette-text-primary)', width: '100%' }}
                variant="outlined"
                defaultValue={DEFAULT_MODEL}
                fullWidth
                value={getValidModel(aiAssistantData.gptModel)}
                onChange={(event) => {
                  const storeState = store.getState();
                  storeState.setGptModel(event.target.value);
                  storeState.clearImageContext();
                }}
                renderValue={(selected) => {
                  const info = getModelInfo(selected);
                  if (!info) return selected;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img src={info.logoPath} alt="Provider logo" style={LOGO_STYLE} />
                      {info.label}
                      {renderCapabilityIcons(info.supportsImages, info.webAccess)}
                    </div>
                  );
                }}
              >
                {renderModelMenuItems()}
              </Select>
            </Grid>

            <Grid item xs={12}>
              <Divider style={{ marginTop: 16 }} />
            </Grid>

            {/* Job Description */}
            <Grid item xs={12} style={SECTION_PADDING}>
              <span><b>Job Description</b></span>
            </Grid>
            <Grid item xs={12}>
              <span style={DESCRIPTION_STYLE}>
                Describe the job or task the AI is to perform
              </span>
            </Grid>
            <Grid item xs={12} style={{ paddingTop: EDITOR_INPUT_GAP }}>
              <FormMentionEditor
                availablePlaceholders={availablePlaceholders}
                text={aiAssistantData.task}
                onChange={(html) => store.getState().setTask(html)}
                {...formMentionEditorProps}
                {...resolvedPlaceholders}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider style={{ marginTop: 16 }} />
            </Grid>

            {/* Context */}
            <Grid item xs={12} style={SECTION_PADDING}>
              <span><b>Context</b></span>
            </Grid>
            <Grid item xs={12} style={SUBSECTION_PADDING}>
              <Grid container alignItems="center">
                <TextFieldsIcon />
                <span><b> Text input</b></span>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <span style={DESCRIPTION_STYLE}>
                (Optional) Add any input for the AI to use when performing its job or task
              </span>
            </Grid>
            <Grid item xs={12} style={{ paddingTop: EDITOR_INPUT_GAP }}>
              <FormMentionEditor
                availablePlaceholders={availablePlaceholders}
                text={aiAssistantData.context}
                onChange={(html) => store.getState().setContext(html)}
                {...formMentionEditorProps}
                {...resolvedPlaceholders}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider style={{ marginTop: 16 }} />
            </Grid>

            {/* Image Input (conditional) */}
            {modelSupportsImages && (
              <>
                <ImageInputComponent
                  aiAssistantData={aiAssistantData}
                  onPlaceholderSelected={props.onPlaceholderSelected}
                  availablePlaceholders={props.availablePlaceholders}
                  store={store}
                />
                <Grid item xs={12}>
                  <Divider style={{ marginTop: 16 }} />
                </Grid>
              </>
            )}

            {/* Output */}
            <Grid item xs={12} style={SECTION_PADDING}>
              <span><b>Output</b></span>
            </Grid>
            <Grid item xs={12}>
              <span style={DESCRIPTION_STYLE}>
                Specify what outputs you want from the AI
              </span>
            </Grid>

            <Grid container item xs={12} direction="column" style={{ paddingTop: 8 }}>
              {aiAssistantData.outputPlaceholders &&
                aiAssistantData.outputPlaceholders.map((aData, index) => (
                  <AIPlaceholderItem
                    key={index}
                    placeholderTypeMap={placeholderTypeMap}
                    index={index}
                    component="ai_assistant_record"
                    aData={aData}
                    aiAssistantData={aiAssistantData}
                    availablePlaceholders={availablePlaceholders}
                    setAIPlaceholder={store.getState().setAIOutputPlaceholders}
                  />
                ))}
            </Grid>

            <Grid item xs={12}>
              <div
                className="query-database-add-data"
                onClick={() => addEmptyPlaceholderValue()}
              >
                <AddIcon /> Add another output
              </div>
            </Grid>

          </Grid>
        </CustomPaper>
      </Grid>
    );
  } else return <IndividualComponentLoader />;
};

export default AIAssistantTemplate;
